import { useMemo, useState } from "react";
import { Pencil, Plus, Sparkles } from "lucide-react";
import { PageHeader } from "../../../components/AppLayout";
import { Button } from "../../../components/shared/Button";
import { Card } from "../../../components/shared/Card";
import { Spinner } from "../../../components/shared/Spinner";
import { Modal } from "../../../components/shared/Modal";
import { FormInput } from "../../../components/shared/FormInput";
import { useResource } from "../../../lib/useResource";
import { useApiCall } from "../../../lib/useToastError";
import {
  AdminGlobalPricing,
  AdminModelPricing,
  type GlobalPricingConfig,
  type ModelPricing,
  type TokenType,
} from "../../../lib/apiTyped";
import {
  TOKEN_TYPES,
  TOKEN_TYPE_LABELS,
  creditsPerUsd,
  effectiveMultiplier,
  fmtMultiplier,
} from "../../../lib/multiplier";

export function AdminModelPricingPage() {
  const { data: pricingData, loading: pricingLoading, refetch: refetchPricing } =
    useResource(() => AdminModelPricing.list(), []);
  const { data: globalData, loading: globalLoading, refetch: refetchGlobal } =
    useResource(() => AdminGlobalPricing.get(), []);

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ModelPricing | null>(null);
  const [seeding, setSeeding] = useState(false);
  const { run } = useApiCall();

  const models = pricingData?.models ?? [];
  const global = globalData?.config ?? null;
  const baseline =
    models.find((m) => m.modelId === global?.baselineModelId) ?? null;

  async function seed() {
    if (seeding) return;
    setSeeding(true);
    const r = await run(() => AdminModelPricing.seed(), {
      successMessage: "Seeded default models.",
    });
    setSeeding(false);
    if (r) {
      await Promise.all([refetchPricing(), refetchGlobal()]);
    }
  }

  const empty = !pricingLoading && models.length === 0;

  return (
    <>
      <PageHeader
        title="Model pricing"
        subtitle="Set the per-model token prices that drive credit consumption. Pricing changes only apply to new turns — historical events are locked to the price at the time they were recorded."
        action={
          <div className="flex gap-2">
            {empty && (
              <Button variant="ghost" onClick={seed} disabled={seeding}>
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  {seeding ? "Seeding…" : "Seed defaults"}
                </span>
              </Button>
            )}
            <Button variant="primary" onClick={() => setCreating(true)}>
              <span className="inline-flex items-center gap-1.5">
                <Plus className="w-4 h-4" />
                Add model
              </span>
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        <GlobalPricingPanel
          global={global}
          loading={globalLoading}
          models={models}
          onSaved={refetchGlobal}
        />

        {pricingLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : models.length === 0 ? (
          <Card className="p-6">
            <p className="text-sm text-ink-2">
              No models configured yet. Click "Seed defaults" to bootstrap the
              catalog with the models platform-agent currently emits, or "Add
              model" to enter one manually.
            </p>
          </Card>
        ) : (
          <ModelPricingTable
            models={models}
            baseline={baseline}
            global={global}
            onEdit={setEditing}
          />
        )}
      </div>

      {creating && (
        <ModelPricingModal
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refetchPricing();
          }}
        />
      )}
      {editing && (
        <ModelPricingModal
          mode="edit"
          model={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refetchPricing();
          }}
        />
      )}
    </>
  );
}

function GlobalPricingPanel({
  global,
  loading,
  models,
  onSaved,
}: {
  global: GlobalPricingConfig | null;
  loading: boolean;
  models: ModelPricing[];
  onSaved: () => Promise<void>;
}) {
  const { run } = useApiCall();
  const [usdPerCredit, setUsdPerCredit] = useState(
    global?.usdPerCredit != null ? String(global.usdPerCredit) : "",
  );
  const [baselineModelId, setBaselineModelId] = useState(
    global?.baselineModelId ?? "",
  );
  const [baselineTokenType, setBaselineTokenType] = useState<TokenType>(
    global?.baselineTokenType ?? "output",
  );
  const [busy, setBusy] = useState(false);

  // Keep local form state in sync the first time the resource resolves.
  useMemo(() => {
    if (!loading && global) {
      setUsdPerCredit(global.usdPerCredit != null ? String(global.usdPerCredit) : "");
      setBaselineModelId(global.baselineModelId ?? "");
      setBaselineTokenType(global.baselineTokenType ?? "output");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function save() {
    if (busy) return;
    const usd = Number(usdPerCredit);
    if (!Number.isFinite(usd) || usd <= 0) return;
    setBusy(true);
    const r = await run(
      () =>
        AdminGlobalPricing.update({
          usdPerCredit: usd,
          baselineModelId: baselineModelId || null,
          baselineTokenType,
        }),
      { successMessage: "Global pricing saved." },
    );
    setBusy(false);
    if (r) await onSaved();
  }

  const usdNumber = Number(usdPerCredit);
  const credits = Number.isFinite(usdNumber) && usdNumber > 0 ? 1 / usdNumber : 0;

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h2 className="text-base font-black text-ink">Credit unit</h2>
        <p className="text-sm text-ink-3 mt-1">
          1 credit equals this many USD of model cost. The baseline below is
          used to display the relative multiplier next to each model — credit
          math always uses the raw USD prices, never the multiplier.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FormInput
          label="USD per credit"
          value={usdPerCredit}
          onChange={setUsdPerCredit}
          type="number"
        />
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Baseline model
          </label>
          <select
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            value={baselineModelId}
            onChange={(e) => setBaselineModelId(e.target.value)}
          >
            <option value="">— none —</option>
            {models.map((m) => (
              <option key={m.modelId} value={m.modelId}>
                {m.displayName} ({m.provider})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Baseline token type
          </label>
          <select
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            value={baselineTokenType}
            onChange={(e) => setBaselineTokenType(e.target.value as TokenType)}
          >
            {TOKEN_TYPES.map((t) => (
              <option key={t} value={t}>
                {TOKEN_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="text-xs text-ink-3">
        {credits > 0
          ? `1 USD ≈ ${creditsPerUsd({ usdPerCredit: usdNumber, baselineModelId: null, baselineTokenType: null }).toLocaleString()} credits`
          : "Enter a positive value to compute the conversion."}
      </div>
      <div>
        <Button variant="primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save credit unit"}
        </Button>
      </div>
    </Card>
  );
}

function ModelPricingTable({
  models,
  baseline,
  global,
  onEdit,
}: {
  models: ModelPricing[];
  baseline: ModelPricing | null;
  global: GlobalPricingConfig | null;
  onEdit: (m: ModelPricing) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg/50 text-left text-xs uppercase tracking-wider text-ink-3">
            <tr>
              <th className="px-4 py-2 font-semibold">Model</th>
              <th className="px-4 py-2 font-semibold">Provider</th>
              {TOKEN_TYPES.map((t) => (
                <th key={t} className="px-4 py-2 font-semibold whitespace-nowrap">
                  {TOKEN_TYPE_LABELS[t]} ($/1M)
                </th>
              ))}
              <th className="px-4 py-2 font-semibold">Output ×</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => {
              const outputMult = effectiveMultiplier(m, "output", baseline, global);
              return (
                <tr
                  key={m.modelId}
                  className="border-t border-border hover:bg-bg/30"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-ink">{m.displayName}</div>
                    <div className="text-xs text-ink-3">{m.modelId}</div>
                  </td>
                  <td className="px-4 py-3 text-ink-2">{m.provider}</td>
                  {TOKEN_TYPES.map((t) => (
                    <td key={t} className="px-4 py-3 text-ink-2 whitespace-nowrap">
                      {fmtPrice(m.prices[t])}
                    </td>
                  ))}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-semibold text-ink">
                      {fmtMultiplier(outputMult)}
                    </span>
                    {outputMult?.source === "override" && (
                      <span className="ml-1 text-xs text-ink-3">(override)</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {m.deprecated ? (
                      <span className="text-xs text-ink-3">deprecated</span>
                    ) : m.isActive ? (
                      <span className="text-xs text-ink">active</span>
                    ) : (
                      <span className="text-xs text-ink-3">inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onEdit(m)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:opacity-80"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function fmtPrice(v: number | undefined): string {
  if (v == null) return "—";
  if (v === 0) return "0";
  if (v < 1) return v.toFixed(4);
  return v.toFixed(2);
}

function ModelPricingModal({
  mode,
  model,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  model?: ModelPricing;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [modelId, setModelId] = useState(model?.modelId ?? "");
  const [provider, setProvider] = useState<ModelPricing["provider"]>(
    model?.provider ?? "anthropic",
  );
  const [displayName, setDisplayName] = useState(model?.displayName ?? "");
  const [prices, setPrices] = useState<Record<TokenType, string>>({
    input: String(model?.prices?.input ?? "0"),
    output: String(model?.prices?.output ?? "0"),
    cachedRead: String(model?.prices?.cachedRead ?? "0"),
    cacheCreation: String(model?.prices?.cacheCreation ?? "0"),
    thinking: String(model?.prices?.thinking ?? "0"),
  });
  const [overrides, setOverrides] = useState<Record<TokenType, string>>({
    input: model?.multiplierOverrides?.input?.toString() ?? "",
    output: model?.multiplierOverrides?.output?.toString() ?? "",
    cachedRead: model?.multiplierOverrides?.cachedRead?.toString() ?? "",
    cacheCreation: model?.multiplierOverrides?.cacheCreation?.toString() ?? "",
    thinking: model?.multiplierOverrides?.thinking?.toString() ?? "",
  });
  const [showOverrides, setShowOverrides] = useState(
    !!model?.multiplierOverrides &&
      Object.values(model.multiplierOverrides).some((v) => v != null),
  );
  const [isActive, setIsActive] = useState(model?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function save() {
    if (busy) return;
    const numericPrices = {} as Record<TokenType, number>;
    for (const t of TOKEN_TYPES) {
      const n = Number(prices[t]);
      if (!Number.isFinite(n) || n < 0) return;
      numericPrices[t] = n;
    }
    const numericOverrides: Partial<Record<TokenType, number>> = {};
    if (showOverrides) {
      for (const t of TOKEN_TYPES) {
        const raw = overrides[t].trim();
        if (raw === "") continue;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) return;
        numericOverrides[t] = n;
      }
    }

    setBusy(true);
    const r = await run(
      () =>
        mode === "create"
          ? AdminModelPricing.create({
              modelId: modelId.trim(),
              provider,
              displayName: displayName.trim(),
              prices: numericPrices,
              multiplierOverrides:
                Object.keys(numericOverrides).length > 0
                  ? numericOverrides
                  : undefined,
              isActive,
            })
          : AdminModelPricing.update(model!.modelId, {
              provider,
              displayName: displayName.trim(),
              prices: numericPrices,
              multiplierOverrides: numericOverrides,
              isActive,
            }),
      {
        successMessage:
          mode === "create" ? "Model added." : "Pricing saved.",
      },
    );
    setBusy(false);
    if (r) onSaved();
  }

  return (
    <Modal onClose={onClose} maxWidth="36rem">
      <h2 className="text-xl font-black mb-1 pr-6 text-ink">
        {mode === "create" ? "Add model pricing" : `Edit ${model?.displayName}`}
      </h2>
      <p className="text-sm text-ink-3 mb-5">
        Prices are USD per 1,000,000 tokens. The credit conversion is computed
        per turn from these prices and the global "USD per credit" setting.
      </p>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            label="Model id"
            value={modelId}
            onChange={setModelId}
            placeholder="claude-opus-4-7"
            disabled={mode === "edit"}
          />
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
              Provider
            </label>
            <select
              className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
              value={provider}
              onChange={(e) =>
                setProvider(e.target.value as ModelPricing["provider"])
              }
            >
              <option value="anthropic">anthropic</option>
              <option value="openai">openai</option>
              <option value="google">google</option>
            </select>
          </div>
        </div>
        <FormInput
          label="Display name"
          value={displayName}
          onChange={setDisplayName}
          placeholder="Claude Opus 4.7"
        />

        <div className="border-t border-border pt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
            Prices ($/1M tokens)
          </div>
          <div className="grid grid-cols-2 gap-3">
            {TOKEN_TYPES.map((t) => (
              <FormInput
                key={t}
                label={TOKEN_TYPE_LABELS[t]}
                value={prices[t]}
                onChange={(v) => setPrices((s) => ({ ...s, [t]: v }))}
                type="number"
              />
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <button
            type="button"
            onClick={() => setShowOverrides((s) => !s)}
            className="text-xs font-semibold uppercase tracking-wider text-ink-2 hover:text-ink"
          >
            {showOverrides ? "Hide" : "Show"} multiplier overrides
          </button>
          {showOverrides && (
            <>
              <p className="text-xs text-ink-3 mt-2 mb-3">
                Override the auto-derived multiplier for one or more token
                types. Display only — does not affect billed credits, which
                always use the prices above.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {TOKEN_TYPES.map((t) => (
                  <FormInput
                    key={t}
                    label={`${TOKEN_TYPE_LABELS[t]} ×`}
                    value={overrides[t]}
                    onChange={(v) => setOverrides((s) => ({ ...s, [t]: v }))}
                    type="number"
                    placeholder="auto"
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <label className="flex items-center justify-between cursor-pointer pt-2">
          <span className="text-sm text-ink-2">Active</span>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 accent-ink"
          />
        </label>
      </div>

      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={save} disabled={busy} fullWidth>
          {busy ? "Saving…" : mode === "create" ? "Add model" : "Save changes"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
